import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import RequestimateForm from "../views/requestimates/requestimateForm";
import WalkInForm from "../views/requestimates/walkInForm";
import { getDraft, lockDraft, unlockDraft } from "../../services/requestimatesService";

/**
 * Route target for /requestimate/draft/:id — resumes a parked draft.
 *
 * Responsibilities (kept out of the wizards themselves):
 *   1. Acquire the single-editor lock. If another user holds it, bounce back to
 *      the list with a message instead of opening the wizard.
 *   2. Load the draft snapshot and decide which wizard to mount (dealer vs
 *      specialist) from draft_payload.wizardType.
 *   3. Hand the wizard its initialDraft + resumeStep + draftId so it hydrates and
 *      opens on the step the user left off at.
 *   4. Release the lock on unmount (best-effort; server TTL is the backstop).
 *
 * All other wizard props are forwarded verbatim from App.js.
 */
class DraftResume extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      payload: null,
      step: 1,
      wizardType: null,
    };
    this._locked = false;
  }

  async componentDidMount() {
    const draftId = this.draftId;

    // 1. Lock — the lock is advisory, so only a genuine 409 held by ANOTHER user
    //    should block. Any other failure (endpoint not ready, 5xx, network, or a
    //    stale lock we ourselves hold) fails open: proceed without the lock rather
    //    than trap the user out of their own draft.
    try {
      await lockDraft(draftId);
      this._locked = true;
    } catch (err) {
      const status = err && err.response && err.response.status;
      const info = (err && err.response && err.response.data) || {};
      const myId = this.props.user && this.props.user.User_id;
      const heldBySomeoneElse =
        status === 409 && info.lockedBy && String(info.lockedBy) !== String(myId);

      if (heldBySomeoneElse) {
        const who = info.lockedByName ? ` by ${info.lockedByName}` : "";
        alert(`This draft is currently being edited${who}. Try again in a few minutes.`);
        this.props.history.push("/requestimates");
        return;
      }
      // Proceed without a held lock. Leave _locked false so we don't later try to
      // release a lock we never acquired.
      console.warn("Draft lock not acquired — continuing without it:", status, err);
    }

    // 2. Load snapshot. Fast path: the list panel already fetched the payload and
    //    handed it to us via router state — use it and skip the round-trip. Only
    //    fall back to GET (deep link / hard refresh) when it isn't there.
    const preloaded =
      this.props.location && this.props.location.state && this.props.location.state.payload;
    if (preloaded && Object.keys(preloaded).length) {
      this.applyPayload(preloaded, preloaded.currentStep);
      return;
    }

    try {
      const draft = await getDraft(draftId);
      const raw = draft && draft.draft_payload;
      const payload = typeof raw === "string" ? JSON.parse(raw) : raw || {};
      if (!payload || !Object.keys(payload).length) {
        throw new Error("Draft snapshot was empty");
      }
      this.applyPayload(payload, draft.draft_step || payload.currentStep);
    } catch (err) {
      const status = err && err.response && err.response.status;
      console.error("Failed to load draft:", status, err);
      alert(
        `Sorry — we couldn't open that draft${status ? ` (server returned ${status})` : ""}.`
      );
      this.props.history.push("/requestimates");
    }
  }

  applyPayload(payload, step) {
    this.setState({
      loading: false,
      payload,
      step: step || payload.currentStep || 1,
      wizardType: payload.wizardType || "dealer",
    });
  }

  componentWillUnmount() {
    if (this._locked) unlockDraft(this.draftId);
  }

  get draftId() {
    return this.props.match.params.id;
  }

  render() {
    const { loading, payload, step, wizardType } = this.state;

    if (loading) {
      return (
        <div className="text-center p-5">
          <i className="fa-sharp fa-regular fa-spinner fa-spin" style={{ fontSize: "24px" }} />
          <div className="text-muted mt-2">Opening draft…</div>
        </div>
      );
    }

    const Wizard = wizardType === "installer" ? WalkInForm : RequestimateForm;
    return (
      <Wizard
        {...this.props}
        draftId={this.draftId}
        initialDraft={payload}
        resumeStep={step}
      />
    );
  }
}

export default withRouter(DraftResume);
